import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from './context/ThemeContext';
import Layout from './components/layout/Layout';
import { API_BASE } from './api';
import './Dashboard.css';

function Dashboard() {
    const [stats, setStats] = useState({
        unassigned: 0,
        completed: 0,
        total: 0
    });
    
    // Performance Overview State
    const [performanceData, setPerformanceData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loadingPerformance, setLoadingPerformance] = useState(false);
    const [filters, setFilters] = useState({
        type: '',
        name: '',
        name_id: '',
        statusFilter: 'all'
    });
    const [availableNames, setAvailableNames] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [hasFilter, setHasFilter] = useState(false);
    const [downloadingCsv, setDownloadingCsv] = useState(false);
    const [displayedCount, setDisplayedCount] = useState(5);
    const [loadingMore, setLoadingMore] = useState(false);
    
    const navigate = useNavigate();
    const { currentTheme } = useContext(ThemeContext);

    const userRole = sessionStorage.getItem('userRole');
    const userId = sessionStorage.getItem('userId');
    const userName = sessionStorage.getItem('userName') || 'User';
    
    const isClient = userRole === 'client' || userRole === 'user';

    useEffect(() => {
        fetchDashboardData();
        if (!isClient && (userRole === 'admin' || userRole === 'manager')) {
            fetchPerformanceData();
        }
    }, []);

    useEffect(() => {
        if (filters.type && !isClient) {
            fetchAvailableNames();
        } else {
            setAvailableNames([]);
        }
    }, [filters.type]);

    useEffect(() => {
        const hasActiveFilter = filters.type && filters.name_id;
        setHasFilter(hasActiveFilter);
        
        if (!isClient && (userRole === 'admin' || userRole === 'manager')) {
            fetchPerformanceData();
        }
    }, [filters.type, filters.name, filters.name_id, filters.statusFilter]);

    // Filter data based on status filter
    useEffect(() => {
        if (performanceData.length > 0) {
            const filtered = performanceData.filter(item => {
                const status = (item.status || '').toLowerCase();
                
                switch (filters.statusFilter) {
                    case 'all':
                        return true;
                    case 'pending_revision':
                        return status.includes('pending') || 
                               status.includes('todo') ||
                               status.includes('in_progress') ||
                               status.includes('inprogress') ||
                               status.includes('assigned') ||
                               status.includes('working') ||
                               status.includes('needs_revision') ||
                               status.includes('revision');
                    case 'completed':
                        return status.includes('completed') || 
                               status.includes('approved');
                    default:
                        return true;
                }
            });
            setFilteredData(filtered);
            setDisplayedCount(5); // Reset to 5 when filters change
        } else {
            setFilteredData([]);
            setDisplayedCount(5); // Reset to 5 when no data
        }
    }, [performanceData, filters.statusFilter]);

    const fetchDashboardData = async () => {
        try {
            if (!refreshing) setLoading(true);
            setError('');

            let operation = 'get_dashboard_stats';
            let url = `${API_BASE}/dashboard_api.php`;
            let requestBody = {};
            
            if (isClient) {
                operation = 'get_client_stats';
                url = `${API_BASE}/client_api.php`;
                requestBody = {
                    operation: operation,
                    client_id: parseInt(userId)
                };
            } else {
                requestBody = {
                    operation: operation,
                    user_role: userRole,
                    user_id: parseInt(userId)
                };
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            const text = await response.text();
            let data;
            
            try {
                data = JSON.parse(text);
            } catch (parseError) {
                throw new Error('Invalid JSON response from server');
            }
            
            if (!response.ok || !data.success) {
                throw new Error(data.error || `Error loading dashboard: HTTP status ${response.status}`);
            }
            
            if (isClient) {
                setStats({
                    unassigned: data.stats.pending || 0,
                    completed: data.stats.completed || data.stats.approved || 0,
                    total: data.stats.total || 0
                });
            } else {
                setStats({
                    unassigned: data.stats.unassigned || 0,
                    completed: data.stats.completed || 0,
                    total: data.stats.total || 0
                });
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Error loading dashboard data: ' + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchPerformanceData = async () => {
        try {
            setLoadingPerformance(true);
            
            const requestBody = {
                operation: 'get_performance_data',
                user_role: userRole,
                user_id: parseInt(userId),
                filter_type: filters.type || '',
                filter_name: filters.name || '',
                filter_name_id: filters.name_id || '',
                status_filter: filters.statusFilter
            };
            
            console.log('Fetching performance data with:', requestBody);
            
            const response = await fetch(`${API_BASE}/performance_api.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestBody)
            });

            const result = await response.json();
            console.log('API Response:', result);
            
            if (result.success) {
                setPerformanceData(result.data || []);
                setHasFilter(result.has_filter || false);
            } else {
                console.error('Error fetching performance data:', result.message);
                setPerformanceData([]);
            }
        } catch (error) {
            console.error('Error fetching performance data:', error);
            setPerformanceData([]);
        } finally {
            setLoadingPerformance(false);
        }
    };

    const fetchAvailableNames = async () => {
        try {
            const response = await fetch(`${API_BASE}/performance_api.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    operation: 'get_available_names',
                    filter_type: filters.type
                })
            });

            const data = await response.json();
            
            if (data.success) {
                setAvailableNames(data.names || []);
            } else {
                setAvailableNames([]);
            }
        } catch (error) {
            console.error('Error fetching available names:', error);
            setAvailableNames([]);
        }
    };

    const handleTypeChange = (type) => {
        setFilters({
            ...filters,
            type: type,
            name: '',
            name_id: ''
        });
    };

    const handleNameChange = (name, nameId) => {
        setFilters({
            ...filters,
            name: name,
            name_id: nameId
        });
    };

    const handleStatusFilterChange = (statusFilter) => {
        setFilters({
            ...filters,
            statusFilter
        });
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchDashboardData();
        if (!isClient) {
            fetchPerformanceData();
        }
    };

    const handleClearFilters = () => {
        setFilters({
            type: '',
            name: '',
            name_id: '',
            statusFilter: 'all'
        });
        setAvailableNames([]);
        setShowFilters(false);
    };

    const getDashboardTitle = () => {
        const titles = {
            admin: 'Admin Dashboard',
            manager: 'Manager Dashboard',
            operator: 'Operator Dashboard',
            editor: 'Editor Dashboard',
            user: 'Client Dashboard',
            client: 'Client Dashboard'
        };
        return titles[userRole] || 'Dashboard';
    };

    const getWelcomeMessage = () => {
        const messages = {
            admin: `Welcome back, ${userName}! Here's an overview of all system activities.`,
            manager: `Welcome back, ${userName}! Manage your team and track progress.`,
            operator: `Welcome back, ${userName}! Here are your assigned tasks and projects.`,
            editor: `Welcome back, ${userName}! Track your editing tasks and deadlines.`,
            user: `Welcome back, ${userName}! Here's an overview of your projects.`,
            client: `Welcome back, ${userName}! Here's an overview of your projects.`
        };
        return messages[userRole] || 'Welcome to your dashboard overview.';
    };
    
    const getMetricLabels = (metricNumber) => {
        if (isClient) {
            switch (metricNumber) {
                case 1: return { label: 'Pending Projects', trend: 'Awaiting start', icon: 'fas fa-clock', color: 'stat-card-1-client' };
                case 2: return { label: 'Completed/Approved', trend: 'Successfully delivered', icon: 'fas fa-check-circle', color: 'stat-card-3-client' };
                case 3: return { label: 'Total Projects', trend: 'All projects', icon: 'fas fa-chart-line', color: 'stat-card-4-client' };
                default: return {};
            }
        } else {
            switch (metricNumber) {
                case 1: return { label: 'Unassigned Tasks', trend: 'Awaiting assignment', icon: 'fas fa-clock', color: 'stat-card-1' };
                case 2: return { label: 'Completed Tasks', trend: 'Successfully finished', icon: 'fas fa-check-circle', color: 'stat-card-3' };
                case 3: return { label: 'Total Tasks', trend: 'All projects', icon: 'fas fa-chart-line', color: 'stat-card-4' };
                default: return {};
            }
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'Invalid date';
        }
    };

    const getStatusColor = (status) => {
        const statusLower = (status || '').toLowerCase();
        if (statusLower.includes('pending') || statusLower.includes('todo')) {
            return '#dc3545';
        } else if (statusLower.includes('progress') || statusLower.includes('assigned') || statusLower.includes('working')) {
            return '#17a2b8';
        } else if (statusLower.includes('revision') || statusLower.includes('needs')) {
            return '#ffc107';
        } else if (statusLower.includes('completed') || statusLower.includes('approved')) {
            return '#28a745';
        } else {
            return '#6c757d';
        }
    };

    const truncateText = (text, maxLength = 50) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const handleLoadMore = () => {
        setLoadingMore(true);
        setTimeout(() => {
            setDisplayedCount(prev => prev + 5);
            setLoadingMore(false);
        }, 300);
    };

    const handleScroll = (e) => {
        const tableContainer = e.target;
        if (tableContainer.scrollHeight - tableContainer.scrollTop <= tableContainer.clientHeight + 100) {
            if (!loadingMore && displayedCount < filteredData.length) {
                handleLoadMore();
            }
        }
    };

    const formatCsvValue = (value) => {
        if (value === null || value === undefined) return '""';
        const str = String(value).replace(/"/g, '""');
        return `"${str}"`;
    };

    const buildCsvContent = (rows) => {
        const headers = [
            'Task ID',
            'Task Name',
            'Source Table',
            'Operator',
            'Editor',
            'Client',
            'Designer',
            'Status',
            'Raw Link',
            'Edited Link',
            'Thumbnail Link',
            'Created At',
            'Assigned At',
            'Due Date'
        ];

        const lines = [headers.map(formatCsvValue).join(',')];

        rows.forEach((task) => {
            const line = [
                task.task_id || task.id || '',
                task.video_name || task.task_name || '',
                task.source_table || '',
                task.operator_name || '',
                task.editor_name || '',
                task.client_name || '',
                task.designer_name || '',
                task.status || '',
                task.raw_link || task.reference_video_link || '',
                task.edited_video_link || task.submitted_url || '',
                task.thumbnail_link || task.thumbnail_url || '',
                task.created_at || '',
                task.assigned_at || '',
                task.due_date || ''
            ].map(formatCsvValue).join(',');

            lines.push(line);
        });

        return lines.join('\n');
    };

    const downloadCsv = async () => {
        try {
            setDownloadingCsv(true);
            const hasTeamMemberSelected = Boolean(filters.type && filters.name_id);
            let rows = filteredData;

            if (!hasTeamMemberSelected) {
                const requestBody = {
                    operation: 'get_performance_data',
                    user_role: userRole,
                    user_id: parseInt(userId),
                    filter_type: filters.type || '',
                    filter_name: filters.name || '',
                    filter_name_id: filters.name_id || '',
                    status_filter: filters.statusFilter,
                    include_all: true
                };

                const response = await fetch(`${API_BASE}/performance_api.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(requestBody)
                });

                const result = await response.json();
                if (!result.success) {
                    throw new Error(result.error || 'Failed to fetch all tasks');
                }
                rows = result.data || [];
            }

            if (!rows || rows.length === 0) {
                setError('No tasks available to download.');
                return;
            }

            const csvContent = buildCsvContent(rows);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const dateStamp = new Date().toISOString().slice(0, 10);
            const scopeLabel = hasTeamMemberSelected ? 'filtered-tasks' : 'all-tasks';
            link.href = url;
            link.download = `performance-${scopeLabel}-${dateStamp}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('CSV download error:', err);
            setError(err.message || 'Failed to download CSV');
        } finally {
            setDownloadingCsv(false);
        }
    };

    const renderLink = (link, label = 'Link') => {
        if (!link || link === '' || link === 'N/A') return 'N/A';
        return (
            <a 
                href={link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="task-link"
                title={link}
            >
                <i className="fas fa-external-link-alt me-1"></i>
                {label}
            </a>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className="dashboard-container">
                    <div className="loading-state">
                        <div className="loading-animation">
                            <div className="loading-dots">
                                <div className="dot"></div>
                                <div className="dot"></div>
                                <div className="dot"></div>
                            </div>
                        </div>
                        <h3>Loading Your Workspace</h3>
                        <p>Preparing your dashboard experience</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const statData = [
        { value: stats.unassigned, ...getMetricLabels(1) },
        { value: stats.completed, ...getMetricLabels(2) },
        { value: stats.total, ...getMetricLabels(3) },
    ];

    return (
        <Layout>
            <div className="dashboard-container pro-fade-in">
                {/* Animated Background */}
                <div className="animated-background">
                    <div className="floating-orb orb-1"></div>
                    <div className="floating-orb orb-2"></div>
                    <div className="floating-orb orb-3"></div>
                    <div className="floating-orb orb-4"></div>
                    <div className="grid-overlay"></div>
                </div>

                {/* Header Section */}
                <div className="dashboard-header">
                    <div className="header-content">
                        <div className="welcome-section">
                            <div className="greeting">
                                <h1 className="dashboard-title">
                                    {getDashboardTitle()}
                                </h1>
                                <div className="welcome-badge">
                                    <span className="badge-text">Hello, {userName}!</span>
                                    <span className="badge-role">{userRole}</span>
                                </div>
                            </div>
                            <p className="welcome-message">{getWelcomeMessage()}</p>
                        </div>
                        <div className="header-actions">
                            <div className="time-display">
                                <div className="current-time">
                                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div className="current-date">
                                    {new Date().toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
                                </div>
                            </div>
                            <button 
                                className="refresh-btn"
                                onClick={handleRefresh}
                                disabled={refreshing}
                            >
                                <div className="refresh-icon">
                                    <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                </div>
                                <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-alert pro-fade-in">
                        <div className="alert-icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div className="alert-message">
                            <strong>Attention Needed</strong>
                            <span>{error}</span>
                        </div>
                        <button className="alert-close" onClick={() => setError('')}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Main Content Grid */}
                <div className="dashboard-content pro-fade-in">
                    {/* Statistics Cards */}
                    <div className="stats-section">
                        <div className="section-title">
                            <h2>{isClient ? 'Project Overview' : 'Performance Overview'}</h2>
                            <p>Real-time task statistics</p>
                        </div>
                        <div className="stats-grid">
                            {statData.map((stat, index) => (
                                <div key={index} className={`stat-card ${stat.color} pro-shadow-lg`}>
                                    <div className="stat-glow"></div>
                                    <div className="stat-content">
                                        <div className="stat-icon">
                                            <i className={stat.icon}></i>
                                        </div>
                                        <div className="stat-info">
                                            <h3 className="stat-number">{stat.value}</h3>
                                            <p className="stat-label">{stat.label}</p>
                                            <span className="stat-trend">{stat.trend}</span>
                                        </div>
                                    </div>
                                    <div className="stat-wave"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Performance Overview Section - Only for Admin/Manager */}
                    {!isClient && (userRole === 'admin' || userRole === 'manager') && (
                        <div className="performance-section pro-fade-in">
                            <div className="section-header">
                                <div>
                                    <h3>Performance Overview</h3>
                                    <p>
                                        {hasFilter ? (
                                            <span>Showing all tasks for selected filter ({filteredData.length} tasks)</span>
                                        ) : (
                                            <span>Showing 5 latest tasks. Use filters to see more.</span>
                                        )}
                                    </p>
                                </div>
                                <div className="filter-badge">
                                    <div className="filter-controls">
                                        <button 
                                            className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                                            onClick={() => setShowFilters(!showFilters)}
                                        >
                                            <i className={`fas ${showFilters ? 'fa-filter-circle-xmark' : 'fa-filter'} me-2`}></i>
                                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                                        </button>
                                        
                                        {hasFilter && (
                                            <button 
                                                className="clear-filters-btn"
                                                onClick={handleClearFilters}
                                            >
                                                <i className="fas fa-times me-2"></i>
                                                Clear Filter
                                            </button>
                                        )}

                                        <button
                                            className="download-csv-btn"
                                            onClick={downloadCsv}
                                            disabled={downloadingCsv || (filters.type && filters.name_id && filteredData.length === 0)}
                                        >
                                            <i className="fas fa-file-csv me-2"></i>
                                            {downloadingCsv ? 'Preparing...' : 'Download CSV'}
                                        </button>
                                        
                                        <span className="badge task-count">
                                            {filteredData.length} {hasFilter ? 'filtered' : 'latest'} tasks
                                        </span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Filters Section - Collapsible */}
                            {showFilters && (
                                <div className="performance-filters">
                                    <div className="filter-group">
                                        <label htmlFor="type-filter">Filter by Team Member Type:</label>
                                        <div className="filter-buttons">
                                            <button 
                                                className={`filter-btn ${filters.type === 'client' ? 'active' : ''}`}
                                                onClick={() => handleTypeChange('client')}
                                            >
                                                <i className="fas fa-user-tie me-2"></i>
                                                Clients
                                            </button>
                                            <button 
                                                className={`filter-btn ${filters.type === 'designer' ? 'active' : ''}`}
                                                onClick={() => handleTypeChange('designer')}
                                            >
                                                <i className="fas fa-paint-brush me-2"></i>
                                                Designers
                                            </button>
                                            <button 
                                                className={`filter-btn ${filters.type === 'editor' ? 'active' : ''}`}
                                                onClick={() => handleTypeChange('editor')}
                                            >
                                                <i className="fas fa-edit me-2"></i>
                                                Editors
                                            </button>
                                            <button 
                                                className={`filter-btn ${filters.type === 'operator' ? 'active' : ''}`}
                                                onClick={() => handleTypeChange('operator')}
                                            >
                                                <i className="fas fa-video me-2"></i>
                                                Operators
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {filters.type && (
                                        <div className="filter-group">
                                            <label htmlFor="name-filter">Select {filters.type}:</label>
                                            <select 
                                                id="name-filter"
                                                className="form-select"
                                                value={filters.name_id}
                                                onChange={(e) => {
                                                    const selectedOption = e.target.options[e.target.selectedIndex];
                                                    handleNameChange(selectedOption.text, e.target.value);
                                                }}
                                                disabled={loadingPerformance || availableNames.length === 0}
                                            >
                                                <option value="">All {filters.type}s</option>
                                                {availableNames.map((name, index) => (
                                                    <option key={index} value={name.id}>
                                                        {name.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {availableNames.length === 0 && !loadingPerformance && (
                                                <small className="text-muted mt-1">No {filters.type}s found</small>
                                            )}
                                        </div>
                                    )}
                                    
                                    <div className="filter-group">
                                        <label htmlFor="status-filter">Filter by Status:</label>
                                        <div className="status-filter-buttons">
                                            <button 
                                                className={`status-filter-btn ${filters.statusFilter === 'all' ? 'active all' : ''}`}
                                                onClick={() => handleStatusFilterChange('all')}
                                            >
                                                <i className="fas fa-list me-2"></i>
                                                All Tasks
                                            </button>
                                            <button 
                                                className={`status-filter-btn ${filters.statusFilter === 'pending_revision' ? 'active pending_revision' : ''}`}
                                                onClick={() => handleStatusFilterChange('pending_revision')}
                                            >
                                                <i className="fas fa-clock me-2"></i>
                                                Pending/Revision
                                            </button>
                                            <button 
                                                className={`status-filter-btn ${filters.statusFilter === 'completed' ? 'active completed' : ''}`}
                                                onClick={() => handleStatusFilterChange('completed')}
                                            >
                                                <i className="fas fa-check-circle me-2"></i>
                                                Completed
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Performance Table - UPDATED: Removed Created column, Added Editor column */}
                            <div className="performance-table-container">
                                {loadingPerformance ? (
                                    <div className="loading-performance">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p>Loading performance data...</p>
                                    </div>
                                ) : filteredData.length > 0 ? (
                                    <div className="table-responsive" onScroll={handleScroll} style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                        <div className="table-info">
                                            <small className="text-muted">
                                                {hasFilter ? `Showing ${Math.min(displayedCount, filteredData.length)} of ${filteredData.length} tasks` : `Showing ${Math.min(displayedCount, filteredData.length)} of ${filteredData.length} tasks. Scroll to load more.`}
                                            </small>
                                        </div>
                                        <table className="table performance-table">
                                            <thead>
                                                <tr>
                                                    <th>Task ID</th>
                                                    <th>Task Name</th>
                                                    <th>Source Table</th>
                                                    <th>Operator</th>
                                                    <th>Editor</th> {/* ADDED EDITOR COLUMN */}
                                                    <th>Client</th>
                                                    <th>Designer</th>
                                                    <th>Status</th>
                                                    <th>Raw Link</th>
                                                    <th>Edited Link</th>
                                                    <th>Thumbnail</th>
                                                    {/* REMOVED: Created column */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredData.slice(0, displayedCount).map((task, index) => (
                                                    <tr key={index}>
                                                        <td>
                                                            <span className="task-id-badge">
                                                                #{task.task_id || task.id}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="task-name">
                                                                <strong>{task.video_name || task.task_name || 'Unnamed Task'}</strong>
                                                                <small className="text-muted d-block">
                                                                    {task.description && truncateText(task.description, 30)}
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="table-badge">
                                                                {task.source_table}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="operator-info" style={{ color: '#ffffff' }}>
                                                                {task.operator_name || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="editor-info" style={{ color: '#ffffff' }}>
                                                                {task.editor_name || 'N/A'}
                                                               
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="client-info">
                                                                {task.client_name || 'N/A'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="designer-info" style={{ color: '#ffffff' }}>
                                                                {task.designer_name || 'N/A'}

                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span 
                                                                className="status-badge"
                                                                style={{ backgroundColor: getStatusColor(task.status) }}
                                                            >
                                                                {task.status?.replace('_', ' ') || 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {renderLink(task.raw_link || task.reference_video_link || '', 'Raw')}
                                                        </td>
                                                        <td>
                                                            {renderLink(task.edited_video_link || task.submitted_url || '', 'Edited')}
                                                        </td>
                                                        <td>
                                                            {renderLink(task.thumbnail_link || task.thumbnail_url || '', 'Thumbnail')}
                                                        </td>
                                                        {/* REMOVED: Created column cell */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {displayedCount < filteredData.length && (
                                            <div className="load-more-container">
                                                <button 
                                                    className="load-more-btn"
                                                    onClick={handleLoadMore}
                                                    disabled={loadingMore}
                                                >
                                                    {loadingMore ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Loading more...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-chevron-down me-2"></i>
                                                            Load More ({filteredData.length - displayedCount} remaining)
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="no-data-message">
                                        {filters.type ? (
                                            <>
                                                <i className="fas fa-search fa-3x mb-3 text-muted"></i>
                                                <h4>No tasks found for selected filter</h4>
                                                <p>Try selecting a different {filters.type} or clear filters</p>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-chart-line fa-3x mb-3 text-muted"></i>
                                                <h4>No tasks available</h4>
                                                <p>Click "Show Filters" to start filtering tasks</p>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Quick Actions - ONLY FOR ADMIN/MANAGER */}
                    {(userRole === 'admin' || userRole === 'manager') && (
                        <div className="actions-section">
                            <div className="section-header">
                                <h3>Quick Actions</h3>
                                <p>Frequently used features</p>
                            </div>
                            <div className="actions-grid">
                                <button 
                                    className="action-btn action-btn-secondary"
                                    onClick={() => navigate('/all-tasks')}
                                >
                                    <div className="action-icon"><i className="fas fa-tasks"></i></div>
                                    <div className="action-content">
                                        <span className="action-title">View All Tasks</span>
                                        <span className="action-desc">Browse projects</span>
                                    </div>
                                    <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
                                </button>

                                <button 
                                    className="action-btn action-btn-accent"
                                    onClick={() => navigate('/unassigned-tasks')}
                                >
                                    <div className="action-icon"><i className="fas fa-user-clock"></i></div>
                                    <div className="action-content">
                                        <span className="action-title">Assign Tasks</span>
                                        <span className="action-desc">Manage assignments</span>
                                    </div>
                                    <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
                                </button>

                                <button 
                                    className="action-btn action-btn-warning"
                                    onClick={() => navigate('/add-users')}
                                >
                                    <div className="action-icon"><i className="fas fa-user-plus"></i></div>
                                    <div className="action-content">
                                        <span className="action-title">Manage Team</span>
                                        <span className="action-desc">Add/Edit users</span>
                                    </div>
                                    <div className="action-arrow"><i className="fas fa-arrow-right"></i></div>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Inline Styles */}
            <style jsx>{`
                .filter-controls {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }
                
                .toggle-filters-btn {
                    background: ${currentTheme.secondary};
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                }
                
                .toggle-filters-btn:hover {
                    background: ${currentTheme.primary};
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
                .toggle-filters-btn.active {
                    background: ${currentTheme.primary};
                    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                }
                
                .clear-filters-btn {
                    background: #6c757d;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                }
                
                .clear-filters-btn:hover {
                    background: #5a6268;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }

                .download-csv-btn {
                    background: #198754;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                }

                .download-csv-btn.secondary {
                    background: #0d6efd;
                }

                .download-csv-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }

                .download-csv-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }
                
                .task-count {
                    background: ${currentTheme.primary};
                    color: white;
                    padding: 8px 16px;
                    border-radius: 20px;
                    font-weight: 600;
                }
                
                .table-info {
                    margin-bottom: 10px;
                    padding: 10px;
                    background: #f8f9fa;
                    border-radius: 4px;
                }
                
                .table-badge {
                    background: #e9ecef;
                    color: #495057;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    text-transform: uppercase;
                    font-family: monospace;
                }
                
                .task-link {
                    color: ${currentTheme.primary};
                    text-decoration: none;
                    font-size: 0.9rem;
                    display: inline-flex;
                    align-items: center;
                    padding: 4px 8px;
                    border-radius: 4px;
                    background: rgba(var(--primary-rgb), 0.1);
                    transition: all 0.3s ease;
                }
                
                .task-link:hover {
                    background: rgba(var(--primary-rgb), 0.2);
                    color: ${currentTheme.primary};
                    text-decoration: underline;
                }
                
                .operator-info, .editor-info, .designer-info {
                    padding: 5px 0;
                }
                
                .status-badge {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 4px;
                    color: white;
                    font-weight: 500;
                    font-size: 0.8rem;
                }

                .load-more-container {
                    padding: 20px;
                    text-align: center;
                    border-top: 1px solid #e9ecef;
                    background: #f8f9fa;
                }

                .load-more-btn {
                    background: ${currentTheme.primary};
                    color: white;
                    border: none;
                    padding: 10px 24px;
                    border-radius: 20px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: inline-flex;
                    align-items: center;
                }

                .load-more-btn:hover:not(:disabled) {
                    background: ${currentTheme.secondary};
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }

                .load-more-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            `}</style>
        </Layout>
    );
}

export default Dashboard;